/* eslint-disable camelcase */
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { getSharedProperties, handleError, setSharedMailboxDebug } from '@utils-global';
import { useAuthStorage } from '@data';
import { AuthMethod } from '../../auth/authMethod';
import { SharedMailboxContext, SharedMailboxContextType } from '../contexts';
import { useToastsContext } from '../hooks';

type Props = {
  children: ReactNode;
};

const MAX_RETRIES = 3;
const RETRY_DELAY = 500; // ms

// Should be used after Office is initialized
export const SharedMailboxContextProvider = ({ children }: Props) => {
  const { graphToken, idToken, authMethod } = useAuthStorage();
  const { showToast } = useToastsContext();
  const [originalUserEmail, setOriginalUserEmail] = useState<string | null>(null);
  const [sharedProperties, setSharedProperties] = useState<Office.SharedProperties | null>(null);
  const [isSharedMailbox, setIsSharedMailbox] = useState(false);

  // Check if item context is available and if it's a shared mailbox with retry mechanism
  useEffect(() => {
    let retryCount = 0;
    let timeoutId: NodeJS.Timeout;

    const checkItemAvailability = () => {
      const { item } = Office.context.mailbox;

      if (item) {
        getSharedProperties()
          .then((properties) => {
            setSharedProperties(properties);
            setIsSharedMailbox(properties !== null);
            setSharedMailboxDebug(properties !== null);
          })
          .catch((error) => {
            setIsSharedMailbox(false);
            setSharedMailboxDebug(false);
            handleError(error);
          });
      } else if (retryCount < MAX_RETRIES) {
        retryCount += 1;
        timeoutId = setTimeout(checkItemAvailability, RETRY_DELAY);
      } else {
        setIsSharedMailbox(false);
        showToast('errors.noEmailSelected', { intent: 'warning' });
      }
    };

    checkItemAvailability();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  useEffect(() => {
    try {
      const signInToken = authMethod === AuthMethod.SSO ? idToken : graphToken;

      if (isSharedMailbox && typeof signInToken === 'string' && typeof graphToken === 'string') {
        const { preferred_username } = jwtDecode<{ preferred_username?: string }>(signInToken);
        const { unique_name, upn } = jwtDecode<{ unique_name?: string; upn?: string }>(graphToken);

        const email = preferred_username || unique_name || upn;

        if (email) setOriginalUserEmail(email);
      }
    } catch (error) {
      handleError(error);
    }
  }, [authMethod, idToken, isSharedMailbox, graphToken]);

  const value: SharedMailboxContextType = useMemo(
    () => ({ isSharedMailbox, sharedProperties, originalUserEmail }),
    [isSharedMailbox, sharedProperties, originalUserEmail],
  );

  return <SharedMailboxContext.Provider value={value}>{children}</SharedMailboxContext.Provider>;
};
